// System Health Panel - Shows database, Redis, Celery, and Sage sync status
//
// Displays real-time health indicators for all system components.

import { useEffect, useState, useCallback } from 'react';
import { systemHealthService } from '../../services/systemHealth';
import type { SystemHealthResponse, ComponentHealth } from '../../services/systemHealth';
import styles from './StatisticsTab.module.css';

const STATUS_COLORS: Record<string, string> = {
  healthy: '#16a34a',
  running: '#3b82f6',
  degraded: '#f59e0b',
  unhealthy: '#dc2626',
  unknown: '#94a3b8',
};

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.375rem',
        padding: '0.25rem 0.625rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'white',
        background: color,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.6)',
        }}
      />
      {status}
    </span>
  );
}

function ComponentCard({ name, health }: { name: string; health: ComponentHealth }) {
  const label = name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className={styles.kpiCard}>
      <span className={styles.kpiLabel}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
        <StatusBadge status={health.status} />
        {health.latency_ms != null && (
          <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
            {health.latency_ms}ms
          </span>
        )}
      </div>
      {health.error && (
        <span
          style={{
            fontSize: '0.75rem',
            color: '#dc2626',
            marginTop: '0.375rem',
            wordBreak: 'break-word',
          }}
        >
          {health.error}
        </span>
      )}
      {health.details && (
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.375rem' }}>
          {Object.entries(health.details).map(([key, val]) => (
            <div key={key}>
              {key.replace(/_/g, ' ')}: <strong>{String(val)}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SystemHealthPanel() {
  const [data, setData] = useState<SystemHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await systemHealthService.getSystemHealth();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <div className={styles.loading}>Loading system health...</div>;

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}

      {data && (
        <>
          {/* Overall Status */}
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Overall Status</span>
              <div style={{ marginTop: '0.25rem' }}>
                <StatusBadge status={data.overall_status} />
              </div>
              <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.375rem' }}>
                {new Date(data.timestamp).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Component Cards */}
          <div className={styles.kpiGrid}>
            {Object.entries(data.components).map(([name, health]) => (
              <ComponentCard key={name} name={name} health={health} />
            ))}
          </div>

          {/* Celery Queues */}
          {data.celery_queues && Object.keys(data.celery_queues).length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Celery Queue Depths</div>
              <div className={styles.statusGrid}>
                {Object.entries(data.celery_queues).map(([queue, depth]) => (
                  <div key={queue} className={styles.statusItem}>
                    <div className={styles.statusItemValue}>{depth}</div>
                    <div className={styles.statusItemLabel}>{queue}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
            <button
              onClick={loadData}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#3b82f6',
                background: 'none',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </div>
        </>
      )}
    </>
  );
}
